/**
 * FeedbackPanel v2 — Admin view of response feedback (thumbs-up/down).
 *
 * Filters: rating · tool · reason · needs-review · date range ·
 * workspace · user. Paginated (50/page). Stats strip from the API.
 * Row actions: View (jump to the tool run) · Versions (history +
 * author an admin correction — becomes current) · Reprocess now ·
 * Resolve review. CSV export of the filtered set.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Download,
  Eye,
  History,
  RotateCcw,
  CheckCircle2,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/apiFetch';

interface FeedbackRow {
  id: string;
  rating: 'up' | 'down';
  created_at: string;
  comment: string | null;
  reason_code: string | null;
  reason_label: string | null;
  content_key: string;
  tool_slug: string | null;
  needs_review: boolean;
  tool_run_id: string;
  workspace_id: string | null;
  user_id: string;
  tool_runs: { input: Record<string, unknown>; status: string } | null;
  workspaces: { name: string } | null;
  users: {
    email: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

interface VersionRow {
  id: string;
  source: string;
  is_current: boolean;
  created_at: string;
  resolved_feedback_id: string | null;
}

type Filter = 'all' | 'up' | 'down';

function questionText(row: FeedbackRow): string {
  try {
    const messages = row.tool_runs?.input?.messages as
      | { role: string; content: string }[]
      | undefined;
    if (!messages) return '—';
    const first = messages.find((m) => m.role === 'user');
    return first?.content?.slice(0, 200) ?? '—';
  } catch {
    return '—';
  }
}

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function runUrl(row: FeedbackRow): string | null {
  if (!row.tool_slug) return null;
  const slug = row.tool_slug;
  const base = slug === 'meeting-summary' ? '/tools/meeting-summary' : null;
  return base ? `${base}?run=${row.tool_run_id}` : null;
}

export const FeedbackPanel: React.FC = () => {
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [total, setTotal] = useState(0);
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const [filter, setFilter] = useState<Filter>('all');
  const [toolFilter, setToolFilter] = useState<string>('all');
  const [reasonFilter, setReasonFilter] = useState<string>('all');
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [reasons, setReasons] = useState<{ code: string; label: string }[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [versionsFor, setVersionsFor] = useState<FeedbackRow | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [correctionText, setCorrectionText] = useState('');
  const [correctionBusy, setCorrectionBusy] = useState(false);

  const load = async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams();
      if (filter !== 'all') p.set('rating', filter);
      if (toolFilter !== 'all') p.set('tool', toolFilter);
      if (reasonFilter !== 'all') p.set('reason', reasonFilter);
      if (needsReviewOnly) p.set('needs_review', 'true');
      if (dateFrom) p.set('from', dateFrom);
      if (dateTo) p.set('to', dateTo);
      if (workspaceSearch) p.set('workspace', workspaceSearch);
      if (userSearch) p.set('user', userSearch);
      p.set('page', String(pageNum));
      p.set('pageSize', String(pageSize));
      const res = await apiFetch(`/api/feedback?${p.toString()}`);
      const j = await res.json();
      if (!res.ok) throw new Error(j.error ?? 'Failed to load feedback');
      setRows(j.data ?? []);
      setTotal(j.total ?? 0);
      setNeedsReviewCount(j.needsReviewCount ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(page);
  }, [
    page,
    filter,
    toolFilter,
    reasonFilter,
    needsReviewOnly,
    dateFrom,
    dateTo,
    workspaceSearch,
    userSearch,
  ]);

  useEffect(() => {
    apiFetch('/api/feedback/reasons')
      .then((r) => r.json())
      .then((d: { data?: Array<{ code: string; label: string }> }) =>
        setReasons(d?.data ?? [])
      )
      .catch(() => {
        /* non-critical */
      });
  }, []);

  const upCount = useMemo(
    () => rows.filter((r) => r.rating === 'up').length,
    [rows]
  );
  const downCount = useMemo(
    () => rows.filter((r) => r.rating === 'down').length,
    [rows]
  );
  const toolSlugs = useMemo(
    () =>
      [...new Set(rows.map((r) => r.tool_slug).filter(Boolean))] as string[],
    [rows]
  );
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const exportCsv = () => {
    const p = new URLSearchParams();
    if (filter !== 'all') p.set('rating', filter);
    if (toolFilter !== 'all') p.set('tool', toolFilter);
    if (reasonFilter !== 'all') p.set('reason', reasonFilter);
    if (dateFrom) p.set('from', dateFrom);
    if (dateTo) p.set('to', dateTo);
    window.location.href = `/api/feedback/export?${p.toString()}`;
  };

  const openVersions = async (row: FeedbackRow) => {
    setVersionsFor(row);
    setCorrectionText('');
    setVersions([]);
    try {
      const res = await apiFetch(
        `/api/feedback/versions?tool_run_id=${row.tool_run_id}${
          row.content_key
            ? `&content_key=${encodeURIComponent(row.content_key)}`
            : ''
        }`
      );
      const j = (await res.json()) as { data?: VersionRow[] };
      setVersions(j.data ?? []);
    } catch {
      setVersions([]);
    }
  };

  const runAction = async (
    row: FeedbackRow,
    action: 'resolve' | 'reprocess'
  ) => {
    setBusyId(row.id);
    try {
      const res = await apiFetch(`/api/feedback/${row.id}/${action}`, {
        method: action === 'resolve' ? 'PATCH' : 'POST',
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(
          (j as { error?: string } | null)?.error ?? `HTTP ${res.status}`
        );
      }
      await load(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const submitCorrection = async () => {
    if (!versionsFor) return;
    let content: unknown;
    try {
      content = JSON.parse(correctionText);
    } catch {
      setError('Correction must be valid JSON (object)');
      return;
    }
    if (!content || typeof content !== 'object') {
      setError('Correction must be a JSON object');
      return;
    }
    setCorrectionBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/feedback/${versionsFor.id}/correction`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(
          (j as { error?: string } | null)?.error ?? `HTTP ${res.status}`
        );
      }
      await openVersions(versionsFor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Correction failed');
    } finally {
      setCorrectionBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Response Feedback</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {total} ratings · {upCount} 👍 / {downCount} 👎 on this page · needs
            review: {needsReviewCount}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            className="gap-1.5"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load(page)}
            disabled={loading}
            className="gap-1.5"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {(
          [
            { key: 'all', label: `All (${total})` },
            { key: 'up', label: `👍 ${upCount}` },
            { key: 'down', label: `👎 ${downCount}` },
          ] as { key: Filter; label: string }[]
        ).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => {
              setFilter(key);
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
              filter === key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}

        <select
          value={toolFilter}
          onChange={(e) => {
            setToolFilter(e.target.value);
            setPage(1);
          }}
          className="h-8 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-600"
        >
          <option value="all">All tools</option>
          {toolSlugs.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={reasonFilter}
          onChange={(e) => {
            setReasonFilter(e.target.value);
            setPage(1);
          }}
          className="h-8 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-600"
        >
          <option value="all">All reasons</option>
          {reasons.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(1);
          }}
          className="h-8 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-600"
          title="From date"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(1);
          }}
          className="h-8 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-600"
          title="To date"
        />
        <input
          placeholder="Workspace…"
          value={workspaceSearch}
          onChange={(e) => {
            setWorkspaceSearch(e.target.value);
            setPage(1);
          }}
          className="h-8 w-32 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-600"
        />
        <input
          placeholder="User…"
          value={userSearch}
          onChange={(e) => {
            setUserSearch(e.target.value);
            setPage(1);
          }}
          className="h-8 w-32 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-600"
        />

        <button
          type="button"
          onClick={() => {
            setNeedsReviewOnly((v) => !v);
            setFilter('all');
            setPage(1);
          }}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
            needsReviewOnly
              ? 'bg-amber-500 text-white border-amber-500'
              : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
          }`}
        >
          Needs review{needsReviewCount > 0 ? ` (${needsReviewCount})` : ''}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      {!error && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700 w-8">
                  Rating
                </th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700">
                  Question
                </th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                  Reason
                </th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                  Workspace
                </th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                  User
                </th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                  Time
                </th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    No feedback yet.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      row.needs_review ? 'bg-amber-50/60' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      {row.rating === 'up' ? (
                        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-green-100">
                          <ThumbsUp className="h-3.5 w-3.5 text-green-600" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-red-100">
                          <ThumbsDown className="h-3.5 w-3.5 text-red-600" />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800 max-w-xs">
                      <p className="line-clamp-2 leading-snug">
                        {questionText(row)}
                      </p>
                      {row.comment && (
                        <p className="text-xs text-gray-500 mt-0.5 italic">
                          "{row.comment}"
                        </p>
                      )}
                      {row.needs_review && (
                        <span className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                          Needs review
                        </span>
                      )}
                      {row.tool_slug && (
                        <span className="mt-1 ml-1 inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                          {row.tool_slug}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {row.reason_label ? (
                        <Badge
                          variant="outline"
                          className="text-[11px] font-normal text-gray-700 whitespace-nowrap"
                        >
                          {row.reason_label}
                        </Badge>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {row.workspaces?.name ? (
                        <Badge
                          variant="outline"
                          className="text-xs font-normal text-gray-700 whitespace-nowrap"
                        >
                          {row.workspaces.name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                      {[row.users?.first_name, row.users?.last_name]
                        .filter(Boolean)
                        .join(' ') ||
                        row.users?.email ||
                        row.user_id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {formatTs(row.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {runUrl(row) && (
                          <a
                            href={runUrl(row) ?? '#'}
                            target="_blank"
                            rel="noreferrer"
                            title="View run"
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:text-blue-600"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        <button
                          type="button"
                          title="Versions"
                          onClick={() => void openVersions(row)}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:text-blue-600"
                        >
                          <History className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          title="Reprocess now"
                          disabled={busyId === row.id}
                          onClick={() => void runAction(row, 'reprocess')}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:text-amber-600"
                        >
                          {busyId === row.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RotateCcw className="h-3 w-3" />
                          )}
                        </button>
                        {row.needs_review && (
                          <button
                            type="button"
                            title="Resolve review"
                            onClick={() => void runAction(row, 'resolve')}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-green-200 text-green-600 hover:bg-green-50"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span>
          {total} total · page {page} of {totalPages}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Versions / correction dialog */}
      {versionsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Versions</h3>
              <button
                type="button"
                onClick={() => setVersionsFor(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              run {versionsFor.tool_run_id.slice(0, 8)}… · key "
              {versionsFor.content_key || 'default'}"
            </p>
            <div className="mt-3 space-y-2 max-h-44 overflow-auto">
              {versions.length === 0 && (
                <p className="text-xs text-gray-400">No versions yet.</p>
              )}
              {versions.map((v) => (
                <div
                  key={v.id}
                  className={`rounded-lg border px-3 py-2 text-xs ${
                    v.is_current
                      ? 'border-blue-200 bg-blue-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">
                      {v.source === 'admin_edit'
                        ? 'Admin correction'
                        : 'AI generated'}
                      {v.is_current ? ' · current' : ''}
                    </span>
                    <span className="text-gray-400">
                      {formatTs(v.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-700 mb-1">
                Author a correction (JSON object — becomes current):
              </p>
              <textarea
                rows={4}
                value={correctionText}
                onChange={(e) => setCorrectionText(e.target.value)}
                placeholder='{ "text": "Corrected answer here…", "sources": [] }'
                className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs text-gray-800"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setVersionsFor(null)}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  disabled={correctionBusy || !correctionText.trim()}
                  onClick={() => void submitCorrection()}
                >
                  {correctionBusy ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Eye className="h-3 w-3 mr-1" />
                  )}
                  Save correction
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
